import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { TeamNotification, NotificationType } from '../utils/types';

// مجموعة Firestore
const NOTIFICATIONS_COLLECTION = 'teamNotifications';

// ===============================
// خدمات الإشعارات
// ===============================

/**
 * إنشاء إشعار جديد
 */
export const createNotification = async (
  notificationData: Omit<TeamNotification, 'id' | 'createdAt'>
): Promise<string> => {
  try {
    const newNotification = {
      ...notificationData,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), newNotification);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * إنشاء إشعار رسالة جديدة
 */
export const createMessageNotification = async (
  userId: string,
  senderName: string,
  chatName: string,
  messageContent: string,
  chatId: string
): Promise<string> => {
  try {
    const notification: Omit<TeamNotification, 'id' | 'createdAt'> = {
      userId,
      type: 'message',
      title: `رسالة جديدة من ${senderName}`,
      content: `في ${chatName}: ${messageContent.substring(0, 100)}${messageContent.length > 100 ? '...' : ''}`,
      data: { chatId, senderName },
      isRead: false,
      actionUrl: `/team/chat/${chatId}`,
    };

    return await createNotification(notification);
  } catch (error) {
    console.error('Error creating message notification:', error);
    throw error;
  }
};

/**
 * إنشاء إشعار ملاحظة جديدة
 */
export const createNoteNotification = async (
  userId: string,
  authorName: string,
  noteTitle: string,
  noteId: string,
  type: 'shared' | 'mentioned' = 'shared'
): Promise<string> => {
  try {
    const notification: Omit<TeamNotification, 'id' | 'createdAt'> = {
      userId,
      type: 'note',
      title: type === 'shared' ? `ملاحظة مشتركة من ${authorName}` : `تم ذكرك في ملاحظة`,
      content: `${noteTitle}`,
      data: { noteId, authorName, type },
      isRead: false,
      actionUrl: `/team/notes/${noteId}`,
    };

    return await createNotification(notification);
  } catch (error) {
    console.error('Error creating note notification:', error);
    throw error;
  }
};

/**
 * إنشاء إشعار منشن
 */
export const createMentionNotification = async (
  userId: string,
  mentionedBy: string,
  content: string,
  contextType: 'message' | 'note',
  contextId: string
): Promise<string> => {
  try {
    const notification: Omit<TeamNotification, 'id' | 'createdAt'> = {
      userId,
      type: 'mention',
      title: `تم ذكرك بواسطة ${mentionedBy}`,
      content: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
      data: { mentionedBy, contextType, contextId },
      isRead: false,
      actionUrl: contextType === 'message' ? `/team/chat/${contextId}` : `/team/notes/${contextId}`,
    };

    return await createNotification(notification);
  } catch (error) {
    console.error('Error creating mention notification:', error);
    throw error;
  }
};

/**
 * إنشاء إشعار نظام
 */
export const createSystemNotification = async (
  userId: string,
  title: string,
  content: string,
  actionUrl?: string
): Promise<string> => {
  try {
    const notification: Omit<TeamNotification, 'id' | 'createdAt'> = {
      userId,
      type: 'system',
      title,
      content,
      data: {},
      isRead: false,
      actionUrl,
    };

    return await createNotification(notification);
  } catch (error) {
    console.error('Error creating system notification:', error);
    throw error;
  }
};

/**
 * الحصول على إشعارات المستخدم
 */
export const getUserNotifications = async (userId: string, limitCount: number = 50): Promise<TeamNotification[]> => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const notifications: TeamNotification[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notifications.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        readAt: data.readAt?.toDate(),
      } as TeamNotification);
    });

    return notifications;
  } catch (error) {
    console.error('Error getting user notifications:', error);
    throw error;
  }
};

/**
 * الحصول على الإشعارات غير المقروءة
 */
export const getUnreadNotifications = async (userId: string): Promise<TeamNotification[]> => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('isRead', '==', false),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const notifications: TeamNotification[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notifications.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        readAt: data.readAt?.toDate(),
      } as TeamNotification);
    });

    return notifications;
  } catch (error) {
    console.error('Error getting unread notifications:', error);
    throw error;
  }
};

/**
 * تمييز إشعار كمقروء
 */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await updateDoc(docRef, {
      isRead: true,
      readAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * تمييز جميع الإشعارات كمقروءة
 */
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );

    const querySnapshot = await getDocs(q);
    const updatePromises: Promise<void>[] = [];

    querySnapshot.forEach((doc) => {
      const docRef = doc.ref;
      updatePromises.push(
        updateDoc(docRef, {
          isRead: true,
          readAt: serverTimestamp(),
        })
      );
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * حذف إشعار
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * حذف جميع الإشعارات المقروءة للمستخدم
 */
export const deleteReadNotifications = async (userId: string): Promise<void> => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('isRead', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const deletePromises: Promise<void>[] = [];

    querySnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });

    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting read notifications:', error);
    throw error;
  }
};

/**
 * الحصول على عدد الإشعارات غير المقروءة
 */
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread notification count:', error);
    throw error;
  }
};

/**
 * الاستماع للإشعارات الجديدة
 */
export const subscribeToNotifications = (
  userId: string,
  callback: (notifications: TeamNotification[]) => void
): (() => void) => {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  return onSnapshot(q, (querySnapshot) => {
    const notifications: TeamNotification[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notifications.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        readAt: data.readAt?.toDate(),
      } as TeamNotification);
    });
    callback(notifications);
  });
};

/**
 * الاستماع لعدد الإشعارات غير المقروءة
 */
export const subscribeToUnreadCount = (
  userId: string,
  callback: (count: number) => void
): (() => void) => {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('userId', '==', userId),
    where('isRead', '==', false)
  );

  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.size);
  });
};
